import { Button, Heading, Text, VStack } from 'native-base';
import React, { ReactNode } from 'react';
import { useMutation } from 'react-query';

import CPAlert from 'app/components/CPAlert';
import HTML from 'app/components/HTML';
import useAttraction from 'app/hooks/useAttraction';
import useGuest from 'app/hooks/useGuest';
import useGuestDataQuery from 'app/hooks/useGuestDataQuery';
import useReservationTextTranslationHelper from 'app/hooks/useReservationTextTranslationHelper';
import { useI18n } from 'app/providers/I18nProvider';
import Analytics from 'app/services/Analytics';
import { Events } from 'app/services/Analytics.types';
import { EntitlementReservation } from 'app/services/GuestCenterService.types';
import { cancel as doCancel } from 'app/services/ReservationService';
import { tID } from 'app/services/TestHelper';

export type ReservationCancelProps = {
  children?: ReactNode;
  reservation: EntitlementReservation;
  onSuccess: () => void;
  onAbort: () => void;
};

export default function ReservationCancel({
  reservation,
  onSuccess,
  onAbort,
}: ReservationCancelProps) {
  const { attractionKey, entitlementKey, attractionOrderNumber, passes } = reservation;
  const productContentKey = reservation.productLineup.contentKey ?? '';
  const attractionContentKey = reservation.attractionConfig.contentKey ?? '';

  const { email } = useGuest();
  const userData = useGuestDataQuery();
  const { t } = useI18n();
  const { attractionContent } = useAttraction({
    productContentKey,
    attractionContentKey,
  });
  const rt = useReservationTextTranslationHelper({ attraction: attractionContent });
  const cancel = useMutation(
    'cancelReservation',
    () =>
      doCancel({ email, attractionKey, locator: entitlementKey, attractionOrderNumber, passes }),
    {
      onSuccess: () => {
        Analytics.trackEvent(Events.ReservationCancel, {
          productKey: productContentKey,
          attractionKey: attractionContentKey,
        });
        userData.refetch();
      },
    }
  );

  const handleCancel = () => {
    cancel.mutate();
  };

  if (cancel.isSuccess) {
    return (
      <VStack space="4">
        <Heading>{rt('reservation_cancelled')}</Heading>

        <Text>{rt('reservation_cancelled_info')}</Text>

        <Button isLoading={userData.isFetching} onPress={onSuccess}>
          {t('gen_done') || t('gen_close')}
        </Button>
      </VStack>
    );
  }

  return (
    <VStack space="4" testID={tID('ReservationCancel')}>
      <Heading>{rt('reservation_cancel_title')}</Heading>

      <Text>{rt('reservation_cancel_desc')}</Text>

      {cancel.isError && (
        <CPAlert status="error">
          <HTML color="error" html={`${t('app_error_unknown')}`} />
        </CPAlert>
      )}

      <Button.Group>
        <Button
          isLoading={cancel.isLoading}
          isLoadingText={t<string>('gen_canceling')}
          onPress={handleCancel}
        >
          {rt('reservation_cancel_yes')}
        </Button>
        <Button isDisabled={cancel.isLoading} variant="outline" onPress={onAbort}>
          {rt('reservation_cancel_no')}
        </Button>
      </Button.Group>
    </VStack>
  );
}
